<script>
// Instagram.vue
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { toast } from 'dashboard/helper/toast';
import { metaAuthErrorMessage } from 'dashboard/helper/metaAuthError';
import { required } from '@vuelidate/validators';
import router from '../../../../index';

import instagramClient from 'dashboard/api/channel/instagramClient';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      instagramPageId: '',
      instagramPageAccessToken: '',
      isRequestingAuthorization: false,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
  },
  validations: {
    inboxName: { required },
    instagramPageId: { required },
    instagramPageAccessToken: { required },
  },
  methods: {
    // Fluxo OAuth (1.1) — via `Instagram::AuthorizationsController#initiate_oauth_flow`.
    // Ação principal da tela; o form manual abaixo é mantido em paralelo de
    // propósito (ver plano Fase 1, item 1.1 — não remover nesta rodada).
    async requestAuthorization() {
      try {
        this.isRequestingAuthorization = true;
        const {
          data: { url },
        } = await instagramClient.createAuthorization();

        window.location.href = url;
      } catch (error) {
        toast.error(
          metaAuthErrorMessage(error, this.$t, 'INBOX_MGMT.ADD.INSTAGRAM')
        );
      } finally {
        this.isRequestingAuthorization = false;
      }
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const instagramChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName,
            channel: {
              type: 'instagram',
              instagram_id: this.instagramPageId,
              access_token: this.instagramPageAccessToken,
            },
          }
        );

        if (!instagramChannel || !instagramChannel.id) {
          throw new Error(
            this.$t('INBOX_MGMT.ADD.INSTAGRAM.API.ERROR_MESSAGE')
          );
        }

        router.replace({
          name: 'settings_inbox_finish',
          params: {
            page: 'new',
            inbox_id: instagramChannel.id,
          },
        });
      } catch (error) {
        let errorMessage = this.$t(
          'INBOX_MGMT.ADD.INSTAGRAM.API.ERROR_MESSAGE'
        );

        if (error.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-n-weak bg-n-background h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0"
  >
    <!-- Conexão via OAuth (1.1) — ação principal -->
    <div class="text-center pb-6 mb-6 border-b border-n-weak">
      <h3 class="text-n-slate-12 mb-2">
        {{ $t('INBOX_MGMT.ADD.INSTAGRAM.CONNECT_YOUR_INSTAGRAM_PROFILE') }}
      </h3>
      <p class="text-n-slate-11 mb-4">
        {{ $t('INBOX_MGMT.ADD.INSTAGRAM.HELP') }}
      </p>
      <form @submit.prevent="requestAuthorization">
        <NextButton
          type="submit"
          solid
          blue
          icon="i-ri-instagram-fill"
          :is-loading="isRequestingAuthorization"
          :label="$t('INBOX_MGMT.ADD.INSTAGRAM.CONTINUE_WITH_INSTAGRAM')"
        />
      </form>
    </div>

    <!-- Configuração manual — mantida em paralelo nesta rodada, ver plano Fase 1 -->
    <p class="text-n-slate-11 font-medium mb-4">
      {{ $t('INBOX_MGMT.ADD.INSTAGRAM.MANUAL_SETUP_TITLE') }}
    </p>
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <!-- Inbox Name -->
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.INSTAGRAM.INBOX_NAME.PLACEHOLDER')"
            @blur="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.INSTAGRAM.INBOX_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <!-- Instagram Page ID -->
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.instagramPageId.$error }">
          <span>
            {{ $t('INBOX_MGMT.ADD.INSTAGRAM.PAGE_ID.LABEL') }}
          </span>
          <input
            v-model="instagramPageId"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.INSTAGRAM.PAGE_ID.PLACEHOLDER')"
            @blur="v$.instagramPageId.$touch"
          />
          <span v-if="v$.instagramPageId.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.INSTAGRAM.PAGE_ID.ERROR') }}
          </span>
        </label>
      </div>

      <!-- Page Access Token (SEM MÁSCARA) -->
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.instagramPageAccessToken.$error }">
          <span>
            {{ $t('INBOX_MGMT.ADD.INSTAGRAM.PAGE_ACCESS_TOKEN.LABEL') }}
          </span>
          <input
            v-model="instagramPageAccessToken"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.INSTAGRAM.PAGE_ACCESS_TOKEN.PLACEHOLDER')
            "
            @blur="v$.instagramPageAccessToken.$touch"
          />
          <span v-if="v$.instagramPageAccessToken.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.INSTAGRAM.PAGE_ACCESS_TOKEN.ERROR') }}
          </span>
        </label>
      </div>

      <!-- Submit Button -->
      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.INSTAGRAM.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
